import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  BellOff,
  BellRing,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Layers,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react'
import { useGetPipelineStageCounts, useGetPipelineStageList } from '../hooks/backend/drivers'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { Button } from '../lib/shadcn/button'
import { NativeSelect } from '../lib/shadcn/native-select'
import { cn } from '../lib/shadcn/utils'
import { statusLabel } from '../utils/interviews'
import { DoNotInterviewButton, LiftBlockButton } from '../components/drivers/ApplicantBlockButtons'
import {
  LIVE_ASIDE,
  LIVE_ORDER,
  applicationRoute,
  blockKindLabel,
  canStopInterviewing,
  closedFunnel,
  day,
  figure,
  funnelWidth,
  liftable,
  liveFunnel,
  matchesSearch,
  monthLabel,
  rejectStageLabel,
  reminderLabel,
  scoredLabel,
  splitLabel,
  sumTiles,
  type FunnelRow,
  type ReminderSummary,
  type StageCounts,
  type StageFilter,
  type StageList,
  type StageRow,
  type StageTile,
} from '../utils/pipelineStages'

const PAGE_SIZE = 100
const TOP_KEY = 'pipelineStages.topOpen'

// Whether the tiles are open is a per-viewer convenience. Storage can be
// missing or refuse (a private window), and the page must work either way.
function readTopOpen(): boolean {
  try {
    return window.localStorage.getItem(TOP_KEY) !== 'false'
  } catch {
    return true
  }
}
function writeTopOpen(v: boolean) {
  try {
    window.localStorage.setItem(TOP_KEY, String(v))
  } catch {
    // Not remembered; nothing else depends on it.
  }
}

export default function PipelineStages() {
  const [params, setParams] = useSearchParams()
  const stage = params.get('stage')
  const filter: StageFilter = useMemo(
    () => ({
      month: params.get('month') ?? undefined,
      status: params.get('status') ?? undefined,
      reject_stage: params.get('reject') ?? undefined,
      reason: params.get('reason') ?? undefined,
      block: params.get('block') ?? undefined,
      reminder: params.get('reminder') ?? undefined,
    }),
    [params],
  )
  const [search, setSearch] = useState('')
  const [shown, setShown] = useState(PAGE_SIZE)
  const [noteOpen, setNoteOpen] = useState(false)
  const [topOpen, setTopOpen] = useState(readTopOpen)

  const counts = useGetPipelineStageCounts()
  const list = useGetPipelineStageList()
  const loadCounts = counts.trigger
  const loadList = list.trigger

  const refresh = useCallback(() => {
    void loadCounts({}, { skipCache: true })
  }, [loadCounts])
  useEffect(refresh, [refresh])

  const reloadList = useCallback(() => {
    if (stage) void loadList({ stage, filter }, { skipCache: true })
  }, [stage, filter, loadList])

  useEffect(() => {
    if (!stage) return
    setShown(PAGE_SIZE)
    reloadList()
  }, [stage, reloadList])

  const data = counts.data as StageCounts | null | undefined
  const tiles: StageTile[] = data?.stages ?? []
  const tile = tiles.find((t) => t.key === stage) ?? null
  const listData = list.data as StageList | null | undefined
  const rows = useMemo(() => {
    if (!stage || listData?.stage !== stage) return []
    return (listData.rows ?? []).filter((r) => matchesSearch(r, search))
  }, [listData, stage, search])

  function setParam(changes: Record<string, string | null>) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setParams(next, { replace: true })
  }

  // After the list has arrived, not in the click. At click time the list is
  // not on the page, and when the stage first renders its rows have not loaded,
  // so the page is still too short to scroll the list to the top: the first
  // attempt stopped 300 pixels short.
  const scrollPending = useRef(false)
  const listReady = !!stage && listData?.stage === stage && !list.loading
  useEffect(() => {
    if (!listReady || !scrollPending.current) return
    scrollPending.current = false
    // Instant, not smooth: a smooth scroll is an animation that a re-render or
    // a backgrounded tab can cancel halfway, and a half-done scroll is the bug
    // this exists to fix.
    document.getElementById('stage-list')?.scrollIntoView({ block: 'start' })
  }, [listReady, stage])

  function openStage(key: string) {
    setSearch('')
    const opening = key !== stage
    setParam({ stage: opening ? key : null, month: null, status: null, reject: null, reason: null, block: null, reminder: null })
    // On a phone the list is eight rows of tiles below the tap, so a tap that
    // opens it and does not move there looks like a tap that did nothing.
    scrollPending.current = opening
  }

  // A funnel bar opens a stage, and a sub-bar opens it already filtered.
  // Clicking the open bar again closes the list.
  function isSelected(row: FunnelRow): boolean {
    if (row.stage !== stage) return false
    const keys = ['status', 'reject', 'block'] as const
    return keys.every((k) => (row.params[k] ?? null) === params.get(k))
  }

  function openRow(row: FunnelRow) {
    setSearch('')
    const opening = !isSelected(row)
    setParam({
      stage: opening ? row.stage : null,
      month: null,
      status: opening ? row.params.status ?? null : null,
      reject: opening ? row.params.reject ?? null : null,
      reason: null,
      block: opening ? row.params.block ?? null : null,
      reminder: null,
    })
    scrollPending.current = opening
  }

  function toggleTop() {
    const next = !topOpen
    setTopOpen(next)
    writeTopOpen(next)
  }

  // A block or a lift changes both the list and the tile counts.
  const afterChange = useCallback(() => {
    refresh()
    reloadList()
  }, [refresh, reloadList])

  if (counts.loading && !data) {
    return (
      <div className="flex h-full items-center gap-2 p-6 text-sm text-muted-foreground">
        <Spinner /> Counting every application
      </div>
    )
  }

  if (counts.error && !data) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="m-4 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 md:m-6">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">The pipeline counts could not be loaded.</p>
            <p className="mt-1">This is usually a short database interruption. Try again in a moment.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={refresh}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (data && !data.allowed) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="m-4 flex items-start gap-3 rounded-lg border bg-card p-4 text-sm md:m-6">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
          <p>{data.message}</p>
        </div>
      </div>
    )
  }

  const tileSum = sumTiles(tiles)
  const liveKeys = new Set<string>([...LIVE_ORDER, ...LIVE_ASIDE])
  const liveTotal = sumTiles(tiles.filter((t) => liveKeys.has(t.key)))
  const closedTotal = sumTiles(tiles.filter((t) => t.key === 'rejected' || t.key === 'blocked'))
  const liveRows = liveFunnel(tiles)
  const closedRows = data ? closedFunnel(data) : []
  const aside = tiles.filter((t) => (LIVE_ASIDE as readonly string[]).includes(t.key))
  const expected = data?.applications_excluding_tests ?? null
  const balanced = expected != null && tileSum === expected

  // The page scrolls itself. The cockpit shell is a fixed h-screen box with
  // overflow hidden, so a page that relies on the window to scroll is clipped
  // at the first screenful: on 24 September that hid every name below the
  // tiles.
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-7xl space-y-4 p-4 md:p-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              <Layers className="h-5 w-5 text-[hsl(var(--brand))]" /> Pipeline Stages
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Every driver application, counted once, in the stage it sits in right now.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleTop} aria-expanded={topOpen}>
              {topOpen ? <ChevronUp className="mr-1.5 h-4 w-4" /> : <ChevronDown className="mr-1.5 h-4 w-4" />}
              {topOpen ? 'Hide funnels' : 'Show funnels'}
            </Button>
            <Button variant="outline" size="sm" onClick={refresh} disabled={counts.loading}>
              <RefreshCw className={cn('mr-1.5 h-4 w-4', counts.loading && 'animate-spin')} /> Refresh
            </Button>
          </div>
        </header>

        {topOpen ? (
          <>
            <div
              className={cn(
                'flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg border px-4 py-3 text-sm',
                balanced ? 'bg-card' : 'border-rose-300 bg-rose-50 text-rose-900',
              )}
            >
              <span>
                <span className="text-2xl font-semibold text-[hsl(var(--brand))]">{figure(tileSum)}</span> applications:{' '}
                {figure(liveTotal)} live, {figure(closedTotal)} rejected or blocked
              </span>
              <span className="text-muted-foreground">
                {balanced
                  ? `Matches all ${figure(expected)} applications on record, test records left out.`
                  : `Does not match the ${figure(expected)} applications on record. Tell Luyanda; a status is not mapped.`}
              </span>
            </div>

            {data?.reminders && <RemindersBar r={data.reminders} />}

            <Funnel
              title="Live pipeline"
              subtitle={`${figure(liveTotal)} applications still moving`}
              rows={liveRows}
              tone="live"
              isSelected={isSelected}
              onSelect={openRow}
              footer={
                aside.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3">
                    <span className="mr-1 text-xs text-muted-foreground">Also counted, not a step</span>
                    {aside.map((t) => (
                      <Chip key={t.key} active={t.key === stage} onClick={() => openStage(t.key)}>
                        {t.label} <span className="tabular-nums opacity-70">{figure(t.n)}</span>
                      </Chip>
                    ))}
                  </div>
                )
              }
            />

            <Funnel
              title="Rejected and blocked"
              subtitle={`${figure(closedTotal)} applications closed`}
              rows={closedRows}
              tone="closed"
              isSelected={isSelected}
              onSelect={openRow}
            />

            <HowCounted open={noteOpen} onToggle={() => setNoteOpen((v) => !v)} tiles={tiles} />
          </>
        ) : (
          <CompactStrip tiles={tiles} selected={stage} onSelect={openStage} total={tileSum} />
        )}

        {stage && tile && (
          <section id="stage-list" className="scroll-mt-4 space-y-4 rounded-lg border bg-card p-4">
            <div>
              <h2 className="text-lg font-semibold">
                {tile.label}{' '}
                {!filter.status && !filter.reject_stage && !filter.block && (
                  <span className="font-normal text-muted-foreground">{figure(tile.n)}</span>
                )}
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">{tile.counted_as}</p>
            </div>

            {stage === 'rejected' && (
              <RejectedBreakdown
                data={data}
                rejectStage={filter.reject_stage ?? null}
                reason={filter.reason ?? null}
                onRejectStage={(k) => setParam({ reject: k, reason: null })}
                onReason={(code) => setParam({ reject: code ? 'application' : filter.reject_stage ?? null, reason: code })}
              />
            )}

            {stage === 'blocked' && tile.splits.length > 0 && (
              <ChipRow
                label="Why"
                options={tile.splits.map((s) => ({ key: s.key, label: blockKindLabel(s.key), n: s.n }))}
                value={filter.block ?? null}
                onChange={(k) => setParam({ block: k })}
              />
            )}

            {tile.splits.length > 1 && stage !== 'rejected' && stage !== 'blocked' && (
              <ChipRow
                label="Split"
                options={tile.splits.map((s) => ({ key: s.key, label: splitLabel(s.key, statusLabel), n: s.n }))}
                value={filter.status ?? null}
                onChange={(k) => setParam({ status: k })}
              />
            )}

            {tile.no_response > 0 && (
              <ChipRow
                label="Reminders"
                options={[{ key: 'no_response', label: 'No response after 7 reminders', n: tile.no_response }]}
                value={filter.reminder ?? null}
                onChange={(k) => setParam({ reminder: k })}
              />
            )}

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, phone, email, manager"
                  className="pl-8"
                />
              </div>
              <NativeSelect
                aria-label="Applied month"
                className="w-full sm:w-56"
                value={filter.month ?? ''}
                onChange={(e) => setParam({ month: e.target.value || null })}
              >
                <option value="">Every applied month</option>
                {(listData?.stage === stage ? listData.months ?? [] : []).map((m) => (
                  <option key={m.month} value={m.month}>
                    {monthLabel(m.month)} ({figure(m.n)})
                  </option>
                ))}
              </NativeSelect>
              {list.loading && <Spinner />}
            </div>

            {list.error ? (
              <p className="text-sm text-rose-700">The list could not be loaded. Pick the bar again to retry.</p>
            ) : (
              <PeopleList
                rows={rows}
                stage={stage}
                shown={shown}
                onMore={() => setShown((n) => n + PAGE_SIZE)}
                canBlock={!!data?.can_block}
                canLift={!!data?.can_lift}
                onChanged={afterChange}
              />
            )}
          </section>
        )}

        {!stage && <p className="text-sm text-muted-foreground">Pick a bar to see who is in it.</p>}
      </div>
    </div>
  )
}

function RemindersBar({ r }: { r: ReminderSummary }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border px-4 py-2.5 text-sm',
        r.enabled ? 'bg-card' : 'bg-muted/40 text-muted-foreground',
      )}
    >
      <span className="flex items-center gap-1.5 font-medium text-foreground">
        {r.enabled ? (
          <BellRing className="h-4 w-4 text-[hsl(var(--brand))]" />
        ) : (
          <BellOff className="h-4 w-4 text-muted-foreground" />
        )}
        {r.enabled ? 'Daily reminders are on' : 'Daily reminders are switched off'}
      </span>
      {r.enabled ? (
        <>
          <span>{figure(r.open)} being reminded</span>
          <span>{figure(r.sent_last_24h)} sent in the last 24 hours</span>
          <span>{figure(r.no_response)} not interested after 7 with no answer</span>
          <span>{figure(r.declined)} said no thanks</span>
        </>
      ) : (
        <span>Nobody is being emailed a reminder until they are switched on.</span>
      )}
    </div>
  )
}

// One funnel: a bar per stage, centred, widest at the top of the flow. The
// width ranks (square root scale, see funnelWidth); the number beside it is
// the figure. Sub-bars sit indented under the stage they belong to.
function Funnel({
  title,
  subtitle,
  rows,
  tone,
  isSelected,
  onSelect,
  footer,
}: {
  title: string
  subtitle: string
  rows: FunnelRow[]
  tone: 'live' | 'closed'
  isSelected: (row: FunnelRow) => boolean
  onSelect: (row: FunnelRow) => void
  footer?: ReactNode
}) {
  const max = Math.max(1, ...rows.filter((r) => r.level === 0).map((r) => r.n))
  const bar = (level: 0 | 1) =>
    tone === 'live'
      ? level === 0
        ? 'bg-[hsl(var(--brand))]'
        : 'bg-[hsl(var(--brand))]/35'
      : level === 0
        ? 'bg-slate-500'
        : 'bg-slate-300 dark:bg-slate-600'
  return (
    <section className="rounded-lg border bg-card p-3 sm:p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-1.5">
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </div>
      <ol className="space-y-0.5">
        {rows.map((r) => {
          const w = funnelWidth(r.n, max)
          const on = isSelected(r)
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelect(r)}
                aria-pressed={on}
                className={cn(
                  // Phone: label and number on one line, the bar full width
                  // under them, so no label is cut. Wider: three columns.
                  'grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted/60 sm:grid-cols-[minmax(0,13rem)_1fr_4.5rem]',
                  on && 'bg-[hsl(var(--brand-soft))] hover:bg-[hsl(var(--brand-soft))]',
                )}
              >
                <span className={cn('col-start-1 row-start-1 min-w-0', r.level === 1 ? 'pl-3 text-xs text-muted-foreground' : 'text-sm font-medium')}>
                  <span className="block truncate">{r.label}</span>
                  {r.level === 0 && r.recent != null && r.recent > 0 && (
                    <span className="block truncate text-[11px] font-normal text-muted-foreground">
                      {figure(r.recent)} in the last 30 days
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'col-span-2 row-start-2 flex items-center justify-center sm:col-span-1 sm:col-start-2 sm:row-start-1',
                    r.level === 0 ? 'h-5 sm:h-7' : 'h-3 sm:h-4',
                  )}
                  aria-hidden
                >
                  {w > 0 ? (
                    <span className={cn('h-full rounded-sm', bar(r.level))} style={{ width: `${w}%` }} />
                  ) : (
                    <span className="w-full border-t border-dashed border-muted-foreground/30" />
                  )}
                </span>
                <span
                  className={cn(
                    'col-start-2 row-start-1 text-right tabular-nums sm:col-start-3',
                    r.level === 0 ? 'text-sm font-semibold' : 'text-xs text-muted-foreground',
                    on && 'text-[hsl(var(--brand-soft-foreground))]',
                  )}
                >
                  {figure(r.n)}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
      {footer}
      <p className="mt-2 px-1.5 text-[11px] text-muted-foreground">
        Bar widths use a square root scale so small stages stay visible. The numbers are exact.
      </p>
    </section>
  )
}

// What the top becomes when it is hidden: one scrollable row of stages, so the
// list gets the screen and switching stage is still one tap.
function CompactStrip({
  tiles,
  selected,
  onSelect,
  total,
}: {
  tiles: StageTile[]
  selected: string | null
  onSelect: (key: string) => void
  total: number
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
      <div className="flex w-max items-center gap-1.5 pb-1">
        <span className="mr-1 whitespace-nowrap text-xs text-muted-foreground">{figure(total)} applications</span>
        {tiles.map((t) => (
          <Chip key={t.key} active={t.key === selected} onClick={() => onSelect(t.key)}>
            <span className="whitespace-nowrap">
              {t.label} <span className="tabular-nums opacity-70">{figure(t.n)}</span>
            </span>
          </Chip>
        ))}
      </div>
    </div>
  )
}

function HowCounted({ open, onToggle, tiles }: { open: boolean; onToggle: () => void; tiles: StageTile[] }) {
  return (
    <div className="rounded-lg border bg-card text-sm">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-medium">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} How this is counted
      </button>
      {open && (
        <div className="space-y-3 border-t px-4 py-3 text-muted-foreground">
          <p>
            One row per driver application, so a person with two applications is counted twice. Test records are left
            out. Each application is counted in exactly one place, so the two funnels add up to the total. The one exception is
            active drivers with a training or induction form outstanding: they are counted under Active and also shown
            in Training pending and Induction pending, and not added to the total twice. Last 30 days and older are by
            the date the person applied.
          </p>
          <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-[12rem_1fr]">
            {tiles.map((t) => (
              <div key={t.key} className="contents">
                <dt className="font-medium text-foreground">{t.label}</dt>
                <dd>{t.counted_as}</dd>
              </div>
            ))}
          </dl>
          <p>
            Rejections at the application stage often have no reason stored. The reason shown is, in order: the stored
            reason; not in Gauteng; PrDP not valid; no active Uber account; prior dismissal; scored below the threshold
            on the old rubric. Any reason not stored is marked derived. What is left is shown as reason not recorded,
            and is never guessed.
          </p>
          <p>
            Reminders: someone who has not booked an interview, answered the offer, completed the onboarding form or
            signed the contract gets a reminder 24 hours after the email for that step, then one a day, 7 at most. Each
            reminder has a No thanks button. Saying no thanks closes the application and blocks the person from applying
            again. The seventh reminder warns them, and 24 hours after it, with still no answer, the application is
            closed and the person is blocked the same way. Both show under Blocked as Not interested.
          </p>
        </div>
      )}
    </div>
  )
}

function ChipRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { key: string; label: string; n: number }[]
  value: string | null
  onChange: (key: string | null) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <Chip active={value == null} onClick={() => onChange(null)}>
        All
      </Chip>
      {options.map((o) => (
        <Chip key={o.key} active={value === o.key} onClick={() => onChange(value === o.key ? null : o.key)}>
          {o.label} <span className="tabular-nums opacity-70">{figure(o.n)}</span>
        </Chip>
      ))}
    </div>
  )
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
          : 'bg-card hover:bg-muted',
      )}
    >
      {children}
    </button>
  )
}

function RejectedBreakdown({
  data,
  rejectStage,
  reason,
  onRejectStage,
  onReason,
}: {
  data: StageCounts | null | undefined
  rejectStage: string | null
  reason: string | null
  onRejectStage: (k: string | null) => void
  onReason: (code: string | null) => void
}) {
  const stages = data?.rejected_by_stage ?? []
  const reasons = data?.application_reasons ?? []
  const reasonTotal = reasons.reduce((s, r) => s + r.n, 0)
  return (
    <div className="space-y-3">
      <ChipRow
        label="Stage"
        options={stages.map((s) => ({ key: s.key, label: rejectStageLabel(s.key), n: s.n }))}
        value={rejectStage}
        onChange={onRejectStage}
      />
      {(rejectStage == null || rejectStage === 'application') && reasons.length > 0 && (
        <div className="rounded-md border p-3">
          <p className="mb-2 text-sm font-medium">
            Why applications were rejected <span className="font-normal text-muted-foreground">{figure(reasonTotal)}</span>
          </p>
          <ol className="space-y-1">
            {reasons.map((r, i) => {
              const on = reason === r.code
              return (
                <li key={r.code}>
                  <button
                    type="button"
                    onClick={() => onReason(on ? null : r.code)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted',
                      on && 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]',
                    )}
                  >
                    <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{i + 1}.</span>
                    <span className="min-w-0 flex-1">
                      {r.label}
                      {r.derived && (
                        <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          derived
                        </span>
                      )}
                    </span>
                    <span className="hidden h-1.5 w-32 shrink-0 overflow-hidden rounded bg-muted sm:block" aria-hidden>
                      <span
                        className="block h-full bg-[hsl(var(--brand))]"
                        style={{ width: `${reasonTotal ? (r.n / reasonTotal) * 100 : 0}%` }}
                      />
                    </span>
                    <span className="w-12 shrink-0 text-right tabular-nums">{figure(r.n)}</span>
                  </button>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </div>
  )
}

// What one person's row says beyond the common columns, by stage.
function detailFor(row: StageRow, stage: string): string | null {
  const parts: string[] = []
  if (row.stage === 'blocked') {
    parts.push(row.block_source ?? 'Blocked')
    if (row.block_reason) parts.push(row.block_reason)
  } else if (row.stage === 'interview_2_scored') {
    parts.push(scoredLabel(row))
  } else if (row.stage === 'rejected') {
    if (row.reject_stage === 'application') {
      if (row.reason_label) parts.push(`${row.reason_label}${row.reason_derived ? ' (derived)' : ''}`)
    } else {
      // The stage itself is already under the name. A no show was never scored,
      // so saying no score was recorded would be noise.
      if (row.reject_stage === 'interview_1') {
        parts.push(row.i1_score ? `Interview 1 score ${row.i1_score}` : 'No Interview 1 score recorded')
      }
      if (row.reject_stage === 'interview_2') {
        parts.push(row.i2_score ? `Interview 2 score ${row.i2_score}` : 'No Interview 2 score recorded')
      }
      if (row.status_note) parts.push(row.status_note)
    }
  }
  if (stage === 'training_pending' || stage === 'induction_pending') {
    const missing = [row.training_outstanding && 'training form', row.induction_outstanding && 'induction form'].filter(Boolean)
    if (row.stage === 'active' && missing.length) parts.push(`Active driver, ${missing.join(' and ')} not signed`)
  }
  const reminder = reminderLabel(row)
  if (reminder) parts.push(reminder)
  return parts.length ? parts.join('. ') : null
}

function RowActions({
  row,
  canBlock,
  canLift,
  onChanged,
}: {
  row: StageRow
  canBlock: boolean
  canLift: boolean
  onChanged: () => void
}) {
  if (row.stage === 'blocked') {
    return canLift && row.block_id && liftable(row.block_kind) ? (
      <LiftBlockButton blockId={row.block_id} name={row.full_name} onDone={onChanged} />
    ) : null
  }
  return canBlock && canStopInterviewing(row.current_status) ? (
    <DoNotInterviewButton applicationId={row.application_id} name={row.full_name} onDone={onChanged} compact />
  ) : null
}

function PeopleList({
  rows,
  stage,
  shown,
  onMore,
  canBlock,
  canLift,
  onChanged,
}: {
  rows: StageRow[]
  stage: string
  shown: number
  onMore: () => void
  canBlock: boolean
  canLift: boolean
  onChanged: () => void
}) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">Nobody here with these filters.</p>
  const visible = rows.slice(0, shown)
  const showSplit = ![
    'interview_1_pending', 'interview_1_booked', 'interview_2_pending', 'interview_2_booked', 'active', 'suspended',
    'submitted', 'compliance', 'onboarding_pending', 'training_pending',
  ].includes(stage)
  const showActions = rows.some((r) =>
    r.stage === 'blocked' ? canLift && liftable(r.block_kind) : canBlock && canStopInterviewing(r.current_status),
  )
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Showing {figure(visible.length)} of {figure(rows.length)}, newest application first.
      </p>

      {/* Phone width: one card per person. */}
      <ul className="space-y-2 md:hidden">
        {visible.map((r) => {
          const detail = detailFor(r, stage)
          return (
            <li key={r.application_id} className="rounded-md border p-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <PersonLink row={r} />
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{r.days_in_stage ?? 0} days</span>
              </div>
              {showSplit && <p className="text-xs text-muted-foreground">{statusLabel(r.current_status)}</p>}
              {detail && <p className="mt-1 text-xs">{detail}</p>}
              <p className="mt-1 break-all text-xs text-muted-foreground">
                {[r.primary_phone, r.primary_email].filter(Boolean).join(' | ') || 'No contact details'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Applied {day(r.applied_at)} | Score {r.score ?? 'Not recorded'} | Manager {r.manager_name ?? 'None'}
              </p>
              {showActions && (
                <div className="mt-2">
                  <RowActions row={r} canBlock={canBlock} canLift={canLift} onChanged={onChanged} />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr className="border-b">
              <th className="py-2 pr-3 font-medium">Name</th>
              <th className="py-2 pr-3 font-medium">Phone</th>
              <th className="py-2 pr-3 font-medium">Email</th>
              <th className="py-2 pr-3 font-medium">Applied</th>
              <th className="py-2 pr-3 text-right font-medium">Days in stage</th>
              <th className="py-2 pr-3 text-right font-medium">Score</th>
              <th className="py-2 pr-3 font-medium">Manager</th>
              <th className="py-2 pr-3 font-medium">Detail</th>
              {showActions && <th className="py-2 font-medium">Action</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.application_id} className="border-b align-top last:border-0">
                <td className="py-2 pr-3">
                  <PersonLink row={r} />
                  {showSplit && <div className="text-xs text-muted-foreground">{statusLabel(r.current_status)}</div>}
                </td>
                <td className="whitespace-nowrap py-2 pr-3">{r.primary_phone ?? 'None'}</td>
                <td className="max-w-[14rem] truncate py-2 pr-3" title={r.primary_email ?? undefined}>
                  {r.primary_email ?? 'None'}
                </td>
                <td className="whitespace-nowrap py-2 pr-3">{day(r.applied_at)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.days_in_stage ?? 'Not recorded'}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{r.score ?? 'Not recorded'}</td>
                <td className="py-2 pr-3">{r.manager_name ?? 'None'}</td>
                <td className="max-w-[22rem] py-2 pr-3 text-xs">{detailFor(r, stage) ?? ''}</td>
                {showActions && (
                  <td className="whitespace-nowrap py-2">
                    <RowActions row={r} canBlock={canBlock} canLift={canLift} onChanged={onChanged} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > shown && (
        <Button variant="outline" size="sm" onClick={onMore}>
          Show {figure(Math.min(PAGE_SIZE, rows.length - shown))} more
        </Button>
      )}
    </div>
  )
}

function PersonLink({ row }: { row: StageRow }) {
  return (
    <Link
      to={applicationRoute(row.current_status, row.application_id)}
      className="inline-flex items-center gap-1 font-medium text-[hsl(var(--brand))] hover:underline"
    >
      {row.full_name?.trim() || 'Name not recorded'} <ExternalLink className="h-3 w-3" />
    </Link>
  )
}
